import React, { useState, useEffect, ReactNode } from "react";
import { DashboardHeader } from "./DashboardHeader";
import { Sidebar } from "./Sidebar";
import { PublicSidebar } from "@/components/public/PublicSidebar";
import { PublicHeader } from "@/components/public/PublicHeader";
import { Room, RoomVisibility } from "@/types/rooms";
import { ContentItem } from "@/types/content";
import { UsageLimitBanner } from "@/components/shared/UsageLimitBanner";
import { UsageLimitErrorBanner } from "@/components/shared/UsageLimitErrorBanner";
import { PricingDialog } from "./PricingDialog";
import { UsageLimitProvider, useUsageLimitContext } from "@/context/UsageLimitContext";

interface DashboardShellProps {
  children: ReactNode;
  room?: Room | null;
  onShareRoom?: () => void;
  chat?: ContentItem | null;
  onShareChat?: () => void;
  onContentTitleChange?: (newTitle: string) => void;
  showDownloadButton?: boolean;
  onDownload?: () => void;
  headerExtra?: ReactNode;
  headerExtraMobile?: ReactNode;
  isPublicView?: boolean;
}

// Inner component that uses the context
const DashboardShellInner: React.FC<DashboardShellProps> = ({ 
  children, 
  room, 
  onShareRoom,
  chat,
  onShareChat,
  onContentTitleChange,
  showDownloadButton,
  onDownload,
  headerExtra,
  headerExtraMobile,
  isPublicView = false,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { showPricingDialog, setShowPricingDialog } = useUsageLimitContext();

  useEffect(() => {
    const saved = localStorage.getItem("sidebar-open");
    if (saved !== null) setIsSidebarOpen(saved === "true");
  }, []);

  const handleToggleSidebar = () => {
    setIsSidebarOpen((prev) => {
      localStorage.setItem("sidebar-open", String(!prev));
      return !prev;
    });
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    localStorage.setItem("sidebar-open", "false");
  };

  return (
    <div className="flex min-h-screen w-full bg-background text-foreground">
      {isPublicView ? (
        <PublicSidebar isOpen={isSidebarOpen} onClose={handleCloseSidebar} />
      ) : (
        <Sidebar isOpen={isSidebarOpen} onClose={handleCloseSidebar} />
      )}

      {isSidebarOpen && (
        <button
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={handleCloseSidebar}
          aria-label="Close sidebar overlay"
        />
      )}

      <div
        className={`relative z-0 flex flex-1 flex-col w-full transition-all duration-300 ${
          isSidebarOpen ? "lg:ml-64" : "lg:ml-0"
        }`}
      >
        {isPublicView ? (
          <PublicHeader 
            onToggleSidebar={handleToggleSidebar}
            isSidebarOpen={isSidebarOpen}
          />
        ) : (
          <DashboardHeader 
            onToggleSidebar={handleToggleSidebar} 
            isSidebarOpen={isSidebarOpen}
            room={room}
            onShareRoom={onShareRoom}
            chat={chat}
            onShareChat={onShareChat}
            onContentTitleChange={onContentTitleChange}
            showDownloadButton={showDownloadButton}
            onDownload={onDownload}
            headerExtra={headerExtra}
            headerExtraMobile={headerExtraMobile}
          />
        )}
        {children}
      </div>

      {!isPublicView && (
        <>
          <UsageLimitErrorBanner />
          <UsageLimitBanner onUpgrade={() => setShowPricingDialog(true)} />
          <PricingDialog open={showPricingDialog} onOpenChange={setShowPricingDialog} />
        </>
      )}
    </div>
  );
};

// Outer component that provides the context
export const DashboardShell: React.FC<DashboardShellProps> = (props) => {
  return (
    <UsageLimitProvider>
      <DashboardShellInner {...props} />
    </UsageLimitProvider>
  );
};
